// @flow
import findNestedValue from '@shared/utils/findNestedValue';
import { Controller } from 'react-hook-form';
import type { filePreviewType, fileUploaderType } from '@shared/model/components';
import { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import {
	Avatar,
	Box,
	CircularProgress,
	FormHelperText,
	Grid,
	IconButton,
	LinearProgress,
	Link,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BackupIcon from '@mui/icons-material/Backup';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useI18n } from '@shared/helper/hooks';
import HelperText from '@shared/components/helperText';

function FilePreview({
	id, file, files, onChange, multiple,
	label, isReadOnly, height, downloadLink, keepAsFile,
	onDelete, maxSize, accept, isUploading, onDrop,
	onDropRejected,
}: filePreviewType): React$Node {
	const { translate } = useI18n('finoShared');
	const [filePreview, setFilePreview] = useState(null);

	useEffect(() => {
		if (!keepAsFile) {
			(async () => {
				const result = await downloadLink?.(file);
				setFilePreview(result);
			})();
		} else {
			setFilePreview(URL.createObjectURL(file));
		}
	}, [downloadLink, file, keepAsFile]);

	const deleteFile = () => {
		files.splice(files.indexOf(file), 1);
		onDelete?.(files, file);
		if (multiple) {
			onChange(files);
		} else {
			onChange('');
		}
	};

	return (<Grid item xs={12} sx={{ mb: 2, height: '100%' }}>
		<Paper
			variant={'outlined'}
			square
			sx={{
				backgroundColor: 'transparent',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				alignItems: 'center',
				width: '100%',
				height: '100%',
			}}
		>
			<Link
				{...(id ? { id: `${id}-file-link` }: {})}
				href={filePreview}
				underline={'hover'}
				target={'_blank'}
				rel={'noreferrer'}
				sx={{ my: 2 }}
			>
				<Stack
					spacing={2}
					justifyContent={'center'}
					alignItems={'center'}
				>
					{isUploading ? (<CircularProgress />) : (<Avatar
						src={filePreview}
						alt={keepAsFile ? file.name : file}
						variant={'square'}
						sx={{ width: '143px', height }}
					>
						<InsertDriveFileIcon/>
					</Avatar>)}
					<Box>{multiple ? file.name : label}</Box>
				</Stack>
			</Link>

			<Stack direction={'row'} alignItems={'center'}>
				{!isReadOnly && <Tooltip title={translate('edit')} placement={'top'}>
					<IconButton>
						<Dropzone
							onDrop={(newFile) => {
								onDrop(newFile, file, onChange);
							}}
							onDropRejected={onDropRejected}
							maxSize={maxSize * 1024 * 1024} // MB
							accept={accept}
							multiple={multiple}
							disabled={isUploading || isReadOnly}
						>
							{({
								  getRootProps, getInputProps,
							  }) => (<Box
								{...getRootProps()}
							>
								{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
								<label
									htmlFor={'contained-button-file'}
									{...(id ? { id: `${id}-edit-file` }: {})}
								>
									<input
										{...getInputProps()}
									/>
									<EditIcon sx={{ cursor: 'pointer' }}/>
								</label>
							</Box>)}
						</Dropzone>
					</IconButton>
				</Tooltip>}
				<Tooltip title={translate('view')} placement={'top'}>
					<IconButton
						href={filePreview}
						target={'_blank'}
						rel={'noreferrer'}
						{...(id && { id: `${id}-view-file` })}
					>
						<VisibilityIcon/>
					</IconButton>
				</Tooltip>
				{!isReadOnly && (<Tooltip title={translate('delete')} placement={'top'}>
					<IconButton {...(id && { id: `${id}-delete-file` })} onClick={deleteFile}>
						<DeleteIcon/>
					</IconButton>
				</Tooltip>)}
			</Stack>
		</Paper>
	</Grid>);
}

function FileUploader({
	id,
	name,
	control,
	errors,
	schema,
	label = '',
	description = '',
	isReadOnly = false,
	accept = '.png, .jpg, .jpeg, .zip, .rar, .pdf, .doc, .docx, .ppt, .pptx, .xls, .xlsx',
	multiple = false,
	defaultValue = multiple ? [] : '',
	maxSize = 5,
	setUploaderStatus,
	height = '143px',
	uploadService,
	onSuccess,
	onDelete,
	downloadLink,
	keepAsFile = false,
}: fileUploaderType): React$Node {
	const { translate } = useI18n('finoShared');
	const [isRequired] = useState(schema
		?.describe()?.fields[name]?.tests?.some((test) => test.name === 'required') || false);

	const baseStyle = {
		borderColor: 'text.main', borderStyle: 'dashed', backgroundColor: 'primaryLight.main', '&:hover': {
			backgroundColor: 'primaryLight.dark',
		},
	};

	const focusedStyle = {
		borderColor: 'secondary.main',
	};
	const acceptStyle = {
		borderColor: 'secondary.main', backgroundColor: 'primaryLight.dark',
	};

	const rejectedStyle = {
		borderColor: 'error.main', backgroundColor: 'primaryLight.dark',
	};

	const disableStyle = {
		opacity: '0.5', backgroundColor: 'primaryLight.main', '&:hover': {
			backgroundColor: 'primaryLight.main',
		},
	};

	const [isUploading, setIsUploading] = useState(false);
	const [percent, setPercent] = useState(0);
	const [errorsList, setErrorsList] = useState([]);

	useEffect(() => {
		setUploaderStatus?.(isUploading);
	}, [isUploading, setUploaderStatus]);

	const onDropRejected = (files) => {
		if (files.length > 0) {
			const { errors: invalidErrors } = files[0];
			const { code, message } = invalidErrors[0];
			if (code === 'file-invalid-type') {
				setErrorsList([{
					message: translate('fileInvalidTypeDescription', {
						field: accept,
					}), code: translate('fileInvalidType'),
				}]);
			} else if (code === 'file-too-large') {
				setErrorsList([{
					message: translate('fileInvalidSizeDescription', {
						field: maxSize,
					}), code: translate('fileInvalidType'),
				}]);
			} else {
				setErrorsList([{
					message, code,
				}]);
			}
		}
	};

	const onDrop = (files, oldFile, onChange) => {
		if (files.length > 0) {
			if (keepAsFile) {
				if (!multiple) {
					return onChange(
						files[0],
					);
				}

				return onChange([...oldFile, files[0]]);
			}

			setIsUploading(true);
			uploadService(files[0], (event) => {
				setPercent(Math.round((100 * event.loaded) / event.total));
			})
				.then((response) => {
					onSuccess?.(response);
					if (multiple) {
						onChange([...oldFile, response[0]?.fileId]);
					} else {
						onChange(response[0]?.fileId);
					}
					setErrorsList([]);
					setIsUploading(false);
					setPercent(0);
				})
				.catch(() => {
					setIsUploading(false);
					setPercent(0);
				});
		}
	};

	return (<Controller
		name={name}
		defaultValue={defaultValue}
		control={control}
		render={({ field }) => {
			const foundError = findNestedValue(errors, name);
			const { onChange, value } = field;
			return (<Grid container>
				<Grid item xs={12}>
					{isUploading && (<Box
						sx={{
							display: 'flex', alignItems: 'center', width: '100%',
						}}
					>
						<Box sx={{ width: '100%', mr: 1 }}>
							<LinearProgress
								variant={'determinate'}
								value={percent}
								color={'info'}
							/>
						</Box>
						<Box>
							<Typography
								variant={'body2'}
								color={'text.secondary'}
							>
								{`${Math.round(percent)}%`}
							</Typography>
						</Box>
					</Box>)}
					<Grid
						container
						spacing={1}
						justifyContent={'center'}
					>
						{multiple ? value.map((file) => (<FilePreview
							id={id}
							key={keepAsFile ? file.name : file}
							file={file}
							files={value}
							label={label}
							isReadOnly={isReadOnly}
							onChange={onChange}
							multiple={multiple}
							height={height}
							downloadLink={downloadLink}
							maxSize={maxSize}
							accept={accept}
							isUploading={isUploading}
							setIsUploading={setIsUploading}
							onDrop={onDrop}
							onDropRejected={onDropRejected}
							onDelete={onDelete}
							keepAsFile={keepAsFile}
						/>)) : value && [value].map((file) => (<FilePreview
							id={id}
							key={keepAsFile ? file.name : file}
							file={file}
							files={[value]}
							label={label}
							isReadOnly={isReadOnly}
							onChange={onChange}
							multiple={multiple}
							height={height}
							downloadLink={downloadLink}
							maxSize={maxSize}
							accept={accept}
							isUploading={isUploading}
							setIsUploading={setIsUploading}
							onDrop={onDrop}
							onDropRejected={onDropRejected}
							onDelete={onDelete}
							keepAsFile={keepAsFile}
						/>))}
					</Grid>
				</Grid>
				{(multiple || (!multiple && !value)) && (<Grid item xs={12}>
					<Dropzone
						onDrop={(files) => {
							onDrop(files, value, onChange);
						}}
						onDropRejected={onDropRejected}
						maxSize={maxSize * 1024 * 1024} // MB
						accept={accept}
						multiple={multiple}
						disabled={isUploading || isReadOnly}
					>
						{({
							  getRootProps, getInputProps, isFocused, isDragAccept,
						  }) => (<Box
							{...getRootProps()}
							sx={{
								...baseStyle, ...(isFocused ? focusedStyle : {}), ...(isDragAccept ?
									acceptStyle :
									{}), ...(errorsList.length ? rejectedStyle : {}), ...(isUploading ?
									disableStyle :
									{}),
							}}
						>
							<label
								htmlFor={'contained-button-file'}
								id={id}
							>
								<input
									type={'file'}
									{...getInputProps()}
								/>
								<Box
									sx={{
										p: 1,
										textAlign: 'center',
										cursor: isUploading || isReadOnly ? 'no-drop' : 'pointer',
									}}
								>
									{isUploading ? (<CircularProgress/>) : (<BackupIcon
										color={foundError || errorsList.length > 0 ? 'error' : ''}
										fontSize={'large'}
									/>)}

									<Typography
										paragraph
										component={'h5'}
										variant={'h5'}
										sx={{
											color: foundError || errorsList.length > 0 ?
												'error.main' :
												'',
										}}
									>
										{label}
										{isRequired && ' *'}
									</Typography>
									<Typography
										variant={'subBody1'}
										sx={{
											color: 'text.main',
										}}
									>
										{description}
									</Typography>
								</Box>
							</label>
						</Box>)}
					</Dropzone>
					{foundError && (<FormHelperText error>
						<HelperText message={foundError?.message} />
					</FormHelperText>)}
					{errorsList.map((errorFile) => (<FormHelperText key={errorFile.code} error>
						<HelperText message={errorFile?.message} />
					</FormHelperText>))}
				</Grid>)}
			</Grid>);
		}}
	/>);
}

export default FileUploader;
